import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  browserStore,
  readScreen,
  resolveCalibration,
  withCalibration,
  withoutCalibration,
  type CalibrationStore,
  type ScreenInfo,
} from '../lib/calibration';
import { CalibrationContext, type CalibrationValue } from './calibration-context';

function sameScreen(a: ScreenInfo, b: ScreenInfo) {
  return a.deviceWidth === b.deviceWidth && a.deviceHeight === b.deviceHeight && a.devicePixelRatio === b.devicePixelRatio;
}

const defaultGetScreen = () => readScreen();

export function CalibrationProvider({ children, store, getScreen = defaultGetScreen }: { children: ReactNode; store?: CalibrationStore; getScreen?: () => ScreenInfo }) {
  const s = useMemo(() => store ?? browserStore(), [store]);
  const [screen, setScreen] = useState(getScreen);
  const [raw, setRaw] = useState(() => s.read());

  // 브라우저 확대·다른 모니터로 창 이동은 resize와 devicePixelRatio 변화로 드러난다.
  useEffect(() => {
    const update = () => setScreen((prev) => {
      const next = getScreen();
      return sameScreen(prev, next) ? prev : next;
    });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [getScreen]);

  const save = useCallback((pxPerCm: number) => {
    const next = withCalibration(s.read(), screen, pxPerCm);
    s.write(next);
    setRaw(next);
  }, [s, screen]);

  const reset = useCallback(() => {
    const next = withoutCalibration(s.read(), screen);
    s.write(next);
    setRaw(next);
  }, [s, screen]);

  const value = useMemo<CalibrationValue>(() => ({ status: resolveCalibration(raw, screen), screen, save, reset }), [raw, screen, save, reset]);
  return <CalibrationContext.Provider value={value}>{children}</CalibrationContext.Provider>;
}
